import React, { useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { api } from '../../../api/remembrApi';
import type { InventoryItemResponse } from '../../../api/types';

/**
 * QuantityEditSheet — Stage 2D polish.
 *
 * Modal for updating an item's `quantity` (integer ≥ 1). Backend
 * accepts this on PATCH /users/inventory/{id} per OpenAPI.
 *
 * UX: ±1 stepper buttons flanking a centered number, plus direct
 * keyboard entry for fast bulk edits ("250 paper towels"). Saves
 * an integer; rejects 0 or negatives.
 *
 * Currently itemExtrasSlice keeps quantity in a client-side cache
 * for instant UX. This modal updates the backend too — we accept
 * the brief mismatch window during the network round-trip and
 * resync on next fetch.
 */

const DEFAULT_INITIAL = 1;
const MAX_QUANTITY = 9999;

interface QuantityEditSheetProps {
  visible: boolean;
  item: InventoryItemResponse;
  /** Optional cache value to seed from when item.quantity is undefined
   *  (the itemExtrasSlice client-side cache). */
  cachedQuantity?: number | null;
  onClose: () => void;
  onSaved: (updated: InventoryItemResponse, quantity: number) => void;
}

export const QuantityEditSheet: React.FC<QuantityEditSheetProps> = ({
  visible,
  item,
  cachedQuantity,
  onClose,
  onSaved,
}) => {
  const initial =
    typeof (item as any).quantity === 'number'
      ? (item as any).quantity
      : typeof cachedQuantity === 'number'
        ? cachedQuantity
        : DEFAULT_INITIAL;
  const [value, setValue] = useState<string>(String(Math.max(1, initial)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setValue(String(Math.max(1, initial)));
      setError(null);
    }
  }, [visible, initial]);

  const parsed = Number(value.replace(/[^0-9]/g, '')) || 0;
  const canSave = parsed >= 1 && parsed <= MAX_QUANTITY;

  const adjust = (delta: number) => {
    const next = Math.max(1, Math.min(MAX_QUANTITY, parsed + delta));
    setValue(String(next));
    setError(null);
  };

  const handleSave = async () => {
    if (!item.id) {
      onClose();
      return;
    }
    if (!canSave) {
      setError(
        parsed < 1
          ? 'Quantity must be at least 1.'
          : `Quantity must be ${MAX_QUANTITY} or less.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateInventoryItem(item.id, {
        quantity: parsed,
      });
      const merged: InventoryItemResponse = {
        ...item,
        ...updated,
        quantity: parsed,
      } as InventoryItemResponse;
      Toast.show({ type: 'success', text1: 'Quantity saved' });
      onSaved(merged, parsed);
      onClose();
    } catch (err: any) {
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Could not save quantity.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Update quantity"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="quantity-edit-sheet"
        >
          <View style={styles.header}>
            <Text style={styles.title}>Update quantity</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
              testID="quantity-edit-close"
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <View style={styles.body}>
            <Text style={styles.fieldLabel}>
              How many of &ldquo;{item.item_name ?? 'this item'}&rdquo; do you have?
            </Text>

            <View style={styles.stepperRow}>
              <Pressable
                onPress={() => adjust(-1)}
                disabled={parsed <= 1 || saving}
                accessibilityRole="button"
                accessibilityLabel="Decrease quantity"
                testID="quantity-edit-decrement"
                style={({ pressed }) => [
                  styles.stepBtn,
                  (parsed <= 1 || saving) && styles.stepBtnDisabled,
                  pressed && { opacity: 0.85 },
                ]}
              >
                <MaterialIcons
                  name="remove"
                  size={26}
                  color={parsed <= 1 ? fpColors.outline : fpColors.primary}
                />
              </Pressable>

              <TextInput
                value={value}
                onChangeText={(t) => {
                  // Strip non-numeric chars but allow empty for clearing
                  // before retyping. Empty state is handled by canSave guard.
                  const cleaned = t.replace(/[^0-9]/g, '').slice(0, 4);
                  setValue(cleaned);
                  setError(null);
                }}
                onSubmitEditing={handleSave}
                returnKeyType="done"
                keyboardType="number-pad"
                placeholder="1"
                placeholderTextColor={fpColors.outline}
                style={styles.numberInput}
                accessibilityLabel="Quantity amount"
                testID="quantity-edit-input"
                maxLength={4}
                selectTextOnFocus
              />

              <Pressable
                onPress={() => adjust(1)}
                disabled={parsed >= MAX_QUANTITY || saving}
                accessibilityRole="button"
                accessibilityLabel="Increase quantity"
                testID="quantity-edit-increment"
                style={({ pressed }) => [
                  styles.stepBtn,
                  (parsed >= MAX_QUANTITY || saving) && styles.stepBtnDisabled,
                  pressed && { opacity: 0.85 },
                ]}
              >
                <MaterialIcons
                  name="add"
                  size={26}
                  color={
                    parsed >= MAX_QUANTITY ? fpColors.outline : fpColors.primary
                  }
                />
              </Pressable>
            </View>

            <Text style={styles.helper}>
              Tap +/− to step, or type a number directly.
            </Text>

            {error ? (
              <View style={styles.errorBanner} testID="quantity-edit-error">
                <MaterialIcons
                  name="error-outline"
                  size={16}
                  color={fpColors.error}
                />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              style={styles.cancelBtn}
              testID="quantity-edit-cancel"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <View style={styles.saveBtnWrap} testID="quantity-edit-save">
              <PrimaryCTA
                label={saving ? 'Saving…' : 'Save'}
                onPress={handleSave}
                disabled={saving || !canSave}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  body: { padding: 20, gap: 12 },
  fieldLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingVertical: 8,
  },
  stepBtn: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepBtnDisabled: { opacity: 0.4 },
  numberInput: {
    minWidth: 100,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 28,
    textAlign: 'center',
    color: fpColors.onSurface,
  },
  helper: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    textAlign: 'center',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderRadius: 10,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
  },
  cancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },
  saveBtnWrap: { flex: 1 },
});

export default QuantityEditSheet;
